// src/portal/portalRevisions.js
//
// The revision rail, derived from route 6's `revisions[]` and `markup[]`.
//
// ── WHAT IS NOT HERE, DELIBERATELY ─────────────────────────────────────────
//
// The mock's rail says "2 features changed" on the newest revision. Nothing
// computes that, and it is not a display problem: establishing that the polygon
// in R3 IS the pad from R2 is feature identity across an arbitrary re-upload,
// which the submission pipeline does not track. A plausible-looking number
// there would be a claim about provenance nobody can support. The line is
// dropped rather than stubbed.

/** Status word shown on the newest revision, from the review's status. */
const HEAD_WORD = {
  draft: "DRAFT",
  submitted: "WITH THE REVIEWER",
  changes_requested: "RETURNED",
  approved: "APPROVED",
};

const fmtDay = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * One entry per revision, oldest first.
 *
 * `findings` counts the markup THE CALLER WAS GIVEN. Route 6 filters unfrozen
 * markup out of a submitter's payload, so a submitter's rail counts only
 * findings that have been returned to them -- the privacy rule inherited rather
 * than re-implemented. See planReviewView.js's note on MARKUP_KEYS.
 *
 * @param {object} p
 * @param {Array}  p.revisions   route 6's revisions[]
 * @param {Array}  p.markup      route 6's markup[]
 * @param {number} p.currentRevision
 * @param {string} p.status
 * @returns {Array<{revision:number,name:string,when:string,findings:number,isCurrent:boolean}>}
 */
export function revisionRail({ revisions = [], markup = [], currentRevision, status } = {}) {
  const byRevisionId = new Map();
  for (const m of markup) {
    if (!m?.revision_id) continue;
    byRevisionId.set(m.revision_id, (byRevisionId.get(m.revision_id) ?? 0) + 1);
  }
  const ordered = [...revisions].sort((a, b) => (a.revision ?? 0) - (b.revision ?? 0));

  return ordered.map((rev, i) => {
    const isCurrent = rev.revision === currentRevision;
    const findings = byRevisionId.get(rev.id) ?? 0;
    // A superseded revision's window closes when the NEXT one was submitted.
    // That is a fact already in the data -- no second timestamp needed.
    const next = ordered[i + 1];
    const from = fmtDay(rev.submitted_at);
    const to = next ? fmtDay(next.submitted_at) : null;
    const span = isCurrent
      ? (from ? `Submitted ${from}` : "Not yet submitted")
      : [from, to].filter(Boolean).join(" → ");
    const count = findings === 1 ? "1 finding" : `${findings} findings`;
    return {
      revision: rev.revision,
      name: `R${rev.revision} · ${isCurrent ? (HEAD_WORD[status] ?? String(status ?? "").toUpperCase()) : "RETURNED"}`,
      when: [span, findings > 0 ? count : null].filter(Boolean).join(" · "),
      findings,
      isCurrent,
    };
  });
}
